export type TBannerRouteTarget = {
  route: string;
  params?: Record<string, string>;
};

export type TBannerRouteTargetSource = {
  routeTargets?: unknown;
  route?: unknown;
  routeKey?: unknown;
  routeValue?: unknown;
};

const ROUTE_PARAMETER_PATTERN = /:([a-zA-Z_][a-zA-Z0-9_]*)\??/g;

export function getBannerRouteParameterNames(path: string): string[] {
  return Array.from(path.matchAll(ROUTE_PARAMETER_PATTERN), (match) => match[1]);
}

function parseRawRouteTargets(rawRouteTargets: unknown): unknown {
  if (rawRouteTargets == null || rawRouteTargets === '') {
    return [];
  }

  if (typeof rawRouteTargets === 'string') {
    try {
      return JSON.parse(rawRouteTargets);
    } catch {
      throw new Error('Banner route targets must be valid JSON');
    }
  }

  return rawRouteTargets;
}

export function normalizeBannerRouteTargets(
  rawRouteTargets: unknown,
  namedRoutes: Record<string, string>
): TBannerRouteTarget[] {
  const parsedRouteTargets = parseRawRouteTargets(rawRouteTargets);
  if (!Array.isArray(parsedRouteTargets)) {
    throw new Error('Banner route targets must be an array');
  }

  return parsedRouteTargets.map((rawTarget, index) => {
    if (!rawTarget || typeof rawTarget !== 'object' || Array.isArray(rawTarget)) {
      throw new Error(`Banner route target ${index + 1} must be an object`);
    }

    const { route, params } = rawTarget as Record<string, unknown>;
    if (typeof route !== 'string' || route.trim().length === 0) {
      throw new Error(`Banner route target ${index + 1} must name a route`);
    }

    const normalizedRoute = route.trim();
    const path = namedRoutes[normalizedRoute];
    if (!path) {
      throw new Error(`Unknown banner route: ${normalizedRoute}`);
    }

    if (params == null) {
      return { route: normalizedRoute };
    }
    if (typeof params !== 'object' || Array.isArray(params)) {
      throw new Error(`Banner route target ${index + 1} parameters must be an object`);
    }

    const routeParameterNames = getBannerRouteParameterNames(path);
    const allowedParameters = new Set(routeParameterNames);
    const rawParams = params as Record<string, unknown>;
    for (const [key, value] of Object.entries(rawParams)) {
      if (!allowedParameters.has(key)) {
        throw new Error(`Unknown parameter ${key} for banner route ${normalizedRoute}`);
      }
      if (typeof value !== 'string') {
        throw new Error(`Banner route parameter ${key} must be a string`);
      }
    }

    const normalizedParams: Record<string, string> = {};
    for (const key of routeParameterNames) {
      const value = rawParams[key] as string | undefined;
      if (value && value.length > 0) {
        normalizedParams[key] = value;
      }
    }

    return Object.keys(normalizedParams).length > 0
      ? { route: normalizedRoute, params: normalizedParams }
      : { route: normalizedRoute };
  });
}

export function getBannerRouteTargets(
  source: TBannerRouteTargetSource,
  namedRoutes: Record<string, string>
): TBannerRouteTarget[] {
  if (source.routeTargets !== undefined) {
    return normalizeBannerRouteTargets(source.routeTargets, namedRoutes);
  }

  if (typeof source.route !== 'string' || source.route.trim().length === 0) {
    return [];
  }

  const params =
    typeof source.routeKey === 'string' &&
    source.routeKey.length > 0 &&
    typeof source.routeValue === 'string' &&
    source.routeValue.length > 0
      ? { [source.routeKey]: source.routeValue }
      : undefined;

  return normalizeBannerRouteTargets([{ route: source.route, ...(params ? { params } : {}) }], namedRoutes);
}

export function bannerRouteTargetsEqual(
  left: TBannerRouteTargetSource,
  right: TBannerRouteTargetSource,
  namedRoutes: Record<string, string>
): boolean {
  const stableTargets = (source: TBannerRouteTargetSource) =>
    getBannerRouteTargets(source, namedRoutes)
      .map((target) => JSON.stringify(target))
      .sort();

  return JSON.stringify(stableTargets(left)) === JSON.stringify(stableTargets(right));
}
